import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import api, { fmt, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { Gauge, CircleDollarSign, Receipt, Users, Package, ArrowUpRight, Pencil, Wallet } from "lucide-react";

const stagger = {
  hidden: { opacity: 0, y: 16 },
  show: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] },
  }),
};

function StatCard({ i, icon: Icon, label, value, sub, testid, onEdit }) {
  return (
    <motion.div
      custom={i}
      variants={stagger}
      initial="hidden"
      animate="show"
      className="bg-card border border-border rounded-md p-5 card-glow transition-all duration-200"
      data-testid={testid}
    >
      <div className="flex items-center justify-between mb-4">
        <span className="text-[10px] tracking-[0.25em] text-muted-foreground">{label}</span>
        <div className="flex items-center gap-2">
          {onEdit && (
            <button
              data-testid="edit-shop-total-btn"
              onClick={onEdit}
              title="Set shop total"
              className="text-muted-foreground hover:text-primary transition-colors"
            >
              <Pencil size={12} />
            </button>
          )}
          <Icon size={16} className="text-primary" />
        </div>
      </div>
      <div className="font-display text-2xl lg:text-3xl font-black text-foreground">{value}</div>
      {sub && <div className="text-[11px] text-primary mt-1.5">{sub}</div>}
    </motion.div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [sales, setSales] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [balance, setBalance] = useState(null);
  const [weekly, setWeekly] = useState([]);
  const [shopTotalOpen, setShopTotalOpen] = useState(false);
  const [shopTotalValue, setShopTotalValue] = useState("");
  const [savingTotal, setSavingTotal] = useState(false);

  const reload = () => {
    api.get("/stats/overview").then((r) => setStats(r.data));
    api.get("/sales").then((r) => setSales(r.data));
    api.get("/payouts").then((r) => setPayouts(r.data));
    api.get("/shop/balance").then((r) => setBalance(r.data));
    api.get("/stats/weekly").then((r) => setWeekly(r.data));
  };

  useEffect(() => {
    reload();
    const t = setInterval(() => api.get("/shop/balance").then((r) => setBalance(r.data)), 5000);
    return () => clearInterval(t);
  }, []);

  const saveShopTotal = async (e) => {
    e.preventDefault();
    setSavingTotal(true);
    try {
      await api.post("/shop/total", { target: Number(shopTotalValue) });
      toast.success("Shop total updated");
      setShopTotalOpen(false);
      reload();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
    setSavingTotal(false);
  };

  const isAdmin = user.role === "admin";

  return (
    <div data-testid="dashboard-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="text-[10px] tracking-[0.4em] text-primary mb-2 flex items-center gap-3">
            <span className="h-px w-8 bg-primary/60" />
            {isAdmin ? "SHOP TELEMETRY" : "MY TELEMETRY"} <span className="font-jp text-muted-foreground">計器盤</span>
          </div>
          <h1 className="font-display text-2xl sm:text-3xl font-black tracking-tight uppercase">
            {isAdmin ? "Workshop Overview" : `Yo, ${user.name.split(" ")[0]}`}
          </h1>
        </div>
        <Link
          to="/pos"
          data-testid="dashboard-new-sale-btn"
          className="flex items-center gap-2 bg-primary text-primary-foreground hover:bg-primary/90 font-display text-[11px] tracking-widest px-5 h-11 rounded-sm transition-colors"
        >
          NEW SALE <ArrowUpRight size={14} />
        </Link>
      </div>

      <div className={`grid grid-cols-2 ${isAdmin ? "lg:grid-cols-5" : "lg:grid-cols-4"} gap-4 mb-4`} data-testid="stats-grid">
        {isAdmin ? (
          <>
            <StatCard i={0} icon={Gauge} label="TOTAL REVENUE" value={fmt(stats?.total_revenue)} sub={`${stats?.total_sales ?? 0} sales`} testid="stat-total-revenue" onEdit={() => { setShopTotalValue(stats?.total_revenue ?? 0); setShopTotalOpen(true); }} />
            <StatCard i={1} icon={Wallet} label="SHOP MONEY" value={balance ? fmt(balance.balance) : "—"} sub="live cash on hand" testid="stat-shop-money" />
            <StatCard i={2} icon={Receipt} label="TODAY" value={fmt(stats?.today_revenue)} sub={`${stats?.today_sales ?? 0} sales today`} testid="stat-today" />
            <StatCard i={3} icon={CircleDollarSign} label="UNPAID COMMISSION" value={fmt(stats?.unpaid_commission)} sub={`${fmt(stats?.total_commission)} total earned`} testid="stat-unpaid-commission" />
            <StatCard i={4} icon={Users} label="CREW" value={stats?.mechanics_count ?? "—"} sub={`${stats?.products_count ?? 0} catalog items`} testid="stat-crew" />
          </>
        ) : (
          <>
            <StatCard i={0} icon={Gauge} label="LIFETIME SALES" value={fmt(stats?.lifetime_sales)} sub={`${stats?.sales_count ?? 0} jobs done`} testid="stat-lifetime-sales" />
            <StatCard i={1} icon={Receipt} label="TODAY" value={fmt(stats?.today_revenue)} sub={`${stats?.today_sales ?? 0} sales today`} testid="stat-today" />
            <StatCard i={2} icon={CircleDollarSign} label="COMMISSION OWED" value={fmt(stats?.unpaid_commission)} sub={`${fmt(stats?.lifetime_commission)} lifetime`} testid="stat-commission-owed" />
            <StatCard i={3} icon={Package} label="MY RATE" value={`${stats?.commission_rate ?? user.commission_rate}%`} sub="per sale" testid="stat-my-rate" />
          </>
        )}
      </div>

      <div className="grid lg:grid-cols-12 gap-4">
        <motion.div
          custom={4}
          variants={stagger}
          initial="hidden"
          animate="show"
          className="lg:col-span-7 bg-card border border-border rounded-md p-5"
          data-testid="money-week-chart"
        >
          <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
            <span className="text-[10px] tracking-[0.25em] text-muted-foreground">
              MONEY IN BY DAY <span className="font-jp text-primary/60">曜日別売上</span>
            </span>
            <div className="flex gap-4 text-[9px] tracking-widest text-muted-foreground" data-testid="chart-legend">
              <span className="flex items-center gap-1.5"><span className="inline-block w-4 h-0.5 bg-[#00FF66]" /> PARTS &amp; SERVICE</span>
              <span className="flex items-center gap-1.5"><span className="inline-block w-4 h-0.5 bg-sky-400" /> VEHICLES</span>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={weekly} margin={{ top: 4, right: 4, left: -14, bottom: 0 }}>
              <XAxis dataKey="day" tick={{ fill: "#526357", fontSize: 10 }} axisLine={false} tickLine={false} />
              <YAxis
                tick={{ fill: "#526357", fontSize: 10 }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(v) => (v >= 1000 ? `$${Math.round(v / 100) / 10}k` : `$${v}`)}
              />
              <Tooltip
                contentStyle={{ background: "#0C100D", border: "1px solid #1B261F", fontSize: 11 }}
                labelStyle={{ color: "#00FF66" }}
                formatter={(v, name) => [`$${Number(v).toLocaleString()}`, name === "parts" ? "Parts & Service" : "Vehicles"]}
              />
              <Line type="monotone" dataKey="parts" stroke="#00FF66" strokeWidth={2.5} dot={{ r: 3, fill: "#00FF66" }} activeDot={{ r: 5 }} />
              <Line type="monotone" dataKey="vehicles" stroke="#38BDF8" strokeWidth={2.5} dot={{ r: 3, fill: "#38BDF8" }} activeDot={{ r: 5 }} />
            </LineChart>
          </ResponsiveContainer>
        </motion.div>

        <motion.div
          custom={5}
          variants={stagger}
          initial="hidden"
          animate="show"
          className="lg:col-span-5 bg-card border border-border rounded-md p-5"
          data-testid="recent-sales-card"
        >
          <span className="text-[10px] tracking-[0.25em] text-muted-foreground">RECENT SALES</span>
          <div className="mt-4 space-y-2.5 max-h-[220px] overflow-y-auto pr-1">
            {sales.slice(0, 8).map((s) => (
              <div key={s.id} className="flex items-center justify-between text-xs border-b border-border/60 pb-2.5" data-testid={`recent-sale-${s.id}`}>
                <div className="min-w-0">
                  <div className="text-foreground font-semibold truncate">{s.customer}</div>
                  <div className="text-muted-foreground text-[10px]">
                    {s.mechanic_name} • {s.items.length} item{s.items.length !== 1 ? "s" : ""}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-foreground font-semibold">{fmt(s.total)}</div>
                  <div className={s.commission_paid ? "text-emerald-400 text-[10px]" : "text-amber-400 text-[10px]"}>
                    {s.commission_paid ? "PAID" : `+${fmt(s.commission)} owed`}
                  </div>
                </div>
              </div>
            ))}
            {!sales.length && (
              <div className="text-muted-foreground text-xs py-8 text-center" data-testid="recent-sales-empty">
                No sales recorded yet.
              </div>
            )}
          </div>
        </motion.div>

        <motion.div
          custom={6}
          variants={stagger}
          initial="hidden"
          animate="show"
          className="lg:col-span-12 bg-card border border-border rounded-md p-5"
          data-testid="payout-history-card"
        >
          <span className="text-[10px] tracking-[0.25em] text-muted-foreground">PAYOUT HISTORY</span>
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-muted-foreground text-[10px] tracking-[0.2em] border-b border-border">
                  <th className="pb-2 font-medium">DATE</th>
                  <th className="pb-2 font-medium">MECHANIC</th>
                  <th className="pb-2 font-medium">SALES</th>
                  <th className="pb-2 font-medium text-right">AMOUNT</th>
                </tr>
              </thead>
              <tbody>
                {payouts.slice(0, 6).map((p) => (
                  <tr key={p.id} className="border-b border-border/50" data-testid={`payout-row-${p.id}`}>
                    <td className="py-2.5 text-muted-foreground">{new Date(p.created_at).toLocaleDateString()}</td>
                    <td className="py-2.5 text-foreground">{p.mechanic_name}</td>
                    <td className="py-2.5 text-muted-foreground">{p.sales_count}</td>
                    <td className="py-2.5 text-right text-primary font-semibold">{fmt(p.amount)}</td>
                  </tr>
                ))}
                {!payouts.length && (
                  <tr>
                    <td colSpan={4} className="py-8 text-center text-muted-foreground" data-testid="payouts-empty">
                      No payouts yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </motion.div>
      </div>
      <Dialog open={shopTotalOpen} onOpenChange={setShopTotalOpen}>
        <DialogContent className="bg-[#0C100D] border-primary/30 text-foreground max-w-sm" data-testid="shop-total-modal">
          <DialogHeader>
            <DialogTitle className="font-display tracking-wide">SET SHOP TOTAL</DialogTitle>
          </DialogHeader>
          <form onSubmit={saveShopTotal} className="space-y-4" data-testid="shop-total-form">
            <div>
              <label className="text-[10px] tracking-[0.25em] text-muted-foreground block mb-2">
                LIFETIME SHOP REVENUE $
              </label>
              <Input
                data-testid="shop-total-input"
                type="number"
                min="0"
                step="1"
                required
                value={shopTotalValue}
                onChange={(e) => setShopTotalValue(e.target.value)}
                className="bg-secondary/60 border-input rounded-sm h-11 text-sm"
              />
            </div>
            <p className="text-[10px] text-muted-foreground leading-relaxed">
              The difference is written to the books as a zero-commission "Shop Total Import"
              adjustment, so crew balances and daily stats stay untouched.
            </p>
            <Button
              data-testid="shop-total-save-btn"
              type="submit"
              disabled={savingTotal}
              className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-display text-[11px] tracking-[0.25em] h-12 rounded-sm"
            >
              {savingTotal ? "SAVING…" : "SAVE SHOP TOTAL"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
